import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { LogOut, AlertTriangle, ShieldAlert } from 'lucide-react';
import Sidebar from './Sidebar';
import Header from './Header';
import { useAuth } from '../../hooks/useAuth';

export default function Layout() {
  const navigate = useNavigate();
  const { requirePasswordChange, migratedFrom } = useAuth();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Persist sidebar state in localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const [impersonation, setImpersonation] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Altezza reale dello stack di banner di sistema, misurata invece che
  // hardcoded: su mobile il testo va a capo e i banner superano i 44px,
  // quindi offset fissi facevano sovrapporre i banner e sfasare l'header.
  const bannersRef = useRef(null);
  const [bannerOffset, setBannerOffset] = useState(0);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useLayoutEffect(() => {
    const el = bannersRef.current;
    if (!el) {
      setBannerOffset(0);
      return;
    }
    const update = () => setBannerOffset(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [impersonation, requirePasswordChange, migratedFrom]);

  // Check for impersonation on mount
  useEffect(() => {
    const impersonationData = localStorage.getItem('impersonationActive');
    if (impersonationData) {
      try {
        const data = JSON.parse(impersonationData);
        if (data.active) {
          setImpersonation(data);
        }
      } catch (e) {
        // Invalid data, remove it
        localStorage.removeItem('impersonationActive');
      }
    }
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const exitImpersonation = () => {
    // Restore admin tokens
    const adminTokensBackup = localStorage.getItem('adminTokensBackup');
    if (adminTokensBackup) {
      try {
        const tokens = JSON.parse(adminTokensBackup);
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
      } catch (e) {
        console.error('Error restoring admin tokens:', e);
      }
    }

    // Clear impersonation data
    localStorage.removeItem('impersonationActive');
    localStorage.removeItem('adminTokensBackup');

    // Redirect to admin panel
    window.location.href = '/admin';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner di sistema impilati (altezza misurata via bannersRef) */}
      {(impersonation || requirePasswordChange) && (
        <div ref={bannersRef} className="fixed top-0 left-0 right-0 z-50">
          {/* Impersonation Banner */}
          {impersonation && (
            <div className="bg-amber-500 text-amber-950 px-4 py-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between shadow-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">
                  Stai visualizzando come: <strong>{impersonation.targetUser}</strong>
                  {impersonation.targetOrg && <span> ({impersonation.targetOrg})</span>}
                </span>
              </div>
              <button
                onClick={exitImpersonation}
                className="flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-1.5 rounded-lg font-medium transition-colors flex-shrink-0"
              >
                <LogOut className="w-4 h-4" />
                Torna ad Admin
              </button>
            </div>
          )}

          {/* Password Change Required Banner */}
          {requirePasswordChange && (
            <div className="bg-red-500 text-white px-4 py-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between shadow-lg">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">
                  Cambio password obbligatorio{migratedFrom ? ` (account migrato da ${migratedFrom})` : ''}. Per sicurezza, aggiorna la tua password.
                </span>
              </div>
              <button
                onClick={() => navigate('/settings')}
                className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap flex-shrink-0"
              >
                Cambia password
              </button>
            </div>
          )}
        </div>
      )}

      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        mobileOpen={mobileDrawerOpen}
        onMobileClose={() => setMobileDrawerOpen(false)}
      />

      {/* Header */}
      <Header
        sidebarCollapsed={sidebarCollapsed}
        bannerOffset={bannerOffset}
        onMenuClick={() => setMobileDrawerOpen(true)}
      />

      {/* Overlay drawer mobile */}
      {mobileDrawerOpen && (
        <div
          onClick={() => setMobileDrawerOpen(false)}
          className="fixed inset-0 z-[55] bg-black/50 lg:hidden"
          aria-hidden="true"
        />
      )}

      {/* Main Content */}
      <main
        className={`
          min-h-screen transition-all duration-300 ease-in-out
          pl-0 ${sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-64'}
        `}
        style={{ paddingTop: bannerOffset + 64 }}
      >
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
